import React, { useContext,useEffect,useState } from "react";
import { makeStyles,useTheme } from "@material-ui/core/styles";
import axios from 'axios';
import {
	Typography,
	Button,
	IconButton,
	Tooltip,
	Menu,
	MenuItem,
	Snackbar,
	SnackbarContent,
	LinearProgress,
} from "@material-ui/core";

import { PageContext } from "../../contexts/PageContext";
import { UserContext } from "../../contexts/UserContext";

import AddIcon from "@material-ui/icons/Add";

import UsersList from './UsersList';
import AddUser from './AddUser';
import MfaResetDialog from "./MfaResetDialog";


const useStyles = makeStyles((theme) => ({
	root: {
		maxWidth: 1000,
		marginLeft: "auto",
    marginRight:"auto"
	},
	actionButtons: theme.actionButtons,
})); 

export default function UserManagement(props) {
	const theme = useTheme();
	const classes = useStyles();
	const [users, setUsers] = useState([]);
	const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
	const [mfaResetUser, setMfaResetUser] = useState(null);
	const [mfaResetProcessing, setMfaResetProcessing] = useState(false);
	const pageContext = useContext(PageContext);
	const userContext = useContext(UserContext);

	useEffect(() => {
		getUsers()
	}, []);
	

	const getUsers = () => {
		const options = {
      url: `${process.env.REACT_APP_API_SERVICE_URL}/users`,
      method: 'get',
      headers: userContext.getRequestAuthHeader()
		};
    axios(options)
      .then((res) => {
        setUsers(res.data);
      })
      .catch((err) => { console.log(err); });
  }

	const saveUser = (editingUser, successCallback = null ) => {
		if (!editingUser || !editingUser.id) {
			pageContext.setErrorMessage("Benutzerdaten nicht geladen. Bitte Benutzer neu öffnen.");
			return;
		}

		const options = {
			url: `${process.env.REACT_APP_API_SERVICE_URL}/users/${editingUser.id}`,
			data: editingUser,
      method: 'put',
      headers: userContext.getRequestAuthHeader()
		};
    axios(options)
      .then((res) => {
        getUsers();
				pageContext.setInfoMessage(editingUser.new_password ? "Passwort wurde zurückgesetzt und gespeichert" : "Benutzer wurde gespeichert");
				if (successCallback) {
					successCallback();
				}
      })
			.catch((err) => {
				console.log(err);
				pageContext.setErrorMessage(err.response?.data?.message || "Benutzer konnte nicht gespeichert werden!");
			});
	}
	
	const deleteUser = (editingUser) => {
		pageContext.setConfirmDialog({
			open: true,
			title: "Wirklich löschen?",
			message:
				"Bist du dir sicher, dass du den Benutzer " +
				(editingUser.name ? editingUser.name : "") +
				" löschen möchtest?",
			handleYes: () => {
				const options = {
					url: `${process.env.REACT_APP_API_SERVICE_URL}/users/${editingUser.id}`,
					method: 'delete',
					headers: userContext.getRequestAuthHeader()
				};
				axios(options)
					.then((res) => {
						getUsers();
						pageContext.setInfoMessage("Benutzer wurde gelöscht");
					})
					.catch((err) => {
						console.log(err);
						pageContext.setErrorMessage(
							err.response && err.response.data && err.response.data.message
								? err.response.data.message
								: "Benutzer konnte nicht gelöscht werden!"
						);
					});
				
				pageContext.setConfirmDialog({
					open: false,
				});
			},
			handleNo: () => {
				pageContext.setConfirmDialog({
					open: false,
				});
			},
		});
	};

	const toggleUserActive = (editingUser, nextActive) => {
		pageContext.setConfirmDialog({
			open: true,
			title: nextActive ? "Profil aktivieren?" : "Profil deaktivieren?",
			message:
				"Möchtest du den Benutzer " +
				(editingUser.name ? editingUser.name : "") +
				(nextActive ? " wirklich aktivieren?" : " wirklich deaktivieren?"),
			handleYes: () => {
				const options = {
					url: `${process.env.REACT_APP_API_SERVICE_URL}/users/${editingUser.id}`,
					data: {
						...editingUser,
						active: nextActive,
					},
					method: 'put',
					headers: userContext.getRequestAuthHeader(),
				};
				axios(options)
					.then(() => {
						getUsers();
						pageContext.setInfoMessage(nextActive ? "Benutzer wurde aktiviert" : "Benutzer wurde deaktiviert");
					})
					.catch((err) => {
						console.log(err);
						pageContext.setErrorMessage(
							err.response && err.response.data && err.response.data.message
								? err.response.data.message
								: nextActive ? "Benutzer konnte nicht aktiviert werden!" : "Benutzer konnte nicht deaktiviert werden!"
						);
					});

				pageContext.setConfirmDialog({
					open: false,
				});
			},
			handleNo: () => {
				pageContext.setConfirmDialog({
					open: false,
				});
			},
		});
	};

	const resetUserMfa = (reason) => {
		if (!mfaResetUser) return;
		setMfaResetProcessing(true);
		const options = {
			url: `${process.env.REACT_APP_API_SERVICE_URL}/users/${mfaResetUser.id}/mfa-reset`,
			data: { reason },
			method: 'post',
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then(() => {
				setMfaResetUser(null);
				getUsers();
				pageContext.setInfoMessage("Zwei-Faktor-Anmeldung wurde zurückgesetzt");
			})
			.catch((err) => {
				pageContext.setErrorMessage(
					err.response && err.response.data && err.response.data.message
						? err.response.data.message
						: "Zwei-Faktor-Anmeldung konnte nicht zurückgesetzt werden!"
				);
			})
			.finally(() => setMfaResetProcessing(false));
	};
	
	if (!["administrator", "backoffice"].includes(userContext.user.role))
  	return <></>;

	return (
		<div className={classes.root}>
			<div className={classes.actionButtons}>
				<Button
					variant="contained"
					aria-label="neuen Benutzer anlegen"
					color="primary"
					size="small"
					onClick={() => {
						setAddUserDialogOpen(true);
					}}
				>
					<AddIcon />
					neuen Benutzer anlegen
				</Button>
			</div>
			<Typography variant="h2">Benutzerverwaltung</Typography>
			<UsersList
				users={users}
				saveUser={saveUser}
				deleteUser={deleteUser}
				toggleUserActive={toggleUserActive}
				resetUserMfa={setMfaResetUser}
			/>
			<AddUser
				currentUserRole={userContext.user.role}
				newUserCallback={
					() => {
						getUsers();
					}
				}
				addUserDialogOpen={addUserDialogOpen}
				setAddUserDialogOpen={setAddUserDialogOpen}
				/>
			<MfaResetDialog
				open={Boolean(mfaResetUser)}
				user={mfaResetUser}
				processing={mfaResetProcessing}
				onCancel={() => setMfaResetUser(null)}
				onConfirm={resetUserMfa}
			/>
		</div>
	);
}
